import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import { NAV_TREE, findActiveSubPage, subPageHref } from './navigation/navTree';
import MobileNavOverlay from './navigation/MobileNavOverlay';
import './Header.css';

// Same chevron glyph as the side rail: points right, rotates down when open
const HeaderChevron = ({ open }) => (
  <svg
    className={`header-chevron-icon${open ? ' open' : ''}`}
    viewBox="0 0 24 24"
    width="16"
    height="16"
    aria-hidden="true"
  >
    <path
      d="M8.6 5.6 15 12l-6.4 6.4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// DJB-122: the mobile header is logo → page title (with the DJB-120 chevron
// sub-page reveal; DJB-217 reads it as "Page – Subpage", e.g. "Library –
// Browse") → hamburger, and the hamburger opens the slide-in overlay
// that mirrors the desktop rail. Desktop shows only the title area — CSS
// hides the logo and hamburger there, since the rail carries branding, nav
// and the Settings/user actions (DJB-114/116/117).
const Header = () => {
  const { authData } = useContext(AuthContext);
  const { theme } = useTheme();
  const [areSubPagesVisible, setAreSubPagesVisible] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // DJB-120: the header shows the active page's title with a chevron that
  // reveals its sub-pages inline
  const activePage = NAV_TREE.find((page) => location.pathname.startsWith(page.path));
  const subPages = activePage?.subPages ?? [];
  // DJB-217: the header names the sub-page the user is on ("Library – Browse").
  // The ?tab= deep link (or, DJB-264, the sub-page's own route) is the single
  // source of truth for the active sub-page (the pages read it too,
  // DJB-107/125); without one — or with a stale key the page no longer
  // knows — the pages fall back to their first tab, so the header does the same.
  const activeSubPage = findActiveSubPage(activePage, location);

  // Moving to another page tucks the sub-pages back behind the chevron
  useEffect(() => {
    setAreSubPagesVisible(false);
  }, [activePage?.key]);

  const openSubPage = (subPage) => {
    // Same deep link the side rail uses (DJB-107): the page reads ?tab= and
    // switches to that sub-page (or, DJB-264, the sub-page's own route)
    navigate(subPageHref(activePage, subPage));
    setAreSubPagesVisible(false);
  };

  return (
    <header className="header">
      {/* DJB-122: mobile shows the logo-only mark; CSS hides it on desktop,
          where the rail's brand row carries the logo instead. The light-mode
          header is solid brand-blue, so it takes the white mark; dark mode
          keeps the colored one over the shell gradient. */}
      <div className="header-left">
        <img
          src={theme === 'dark' ? '/DJ-logo.svg' : '/DJ-logo-white.svg'}
          alt="Digitale Jukebox"
          className="header-logo"
        />
      </div>
      {activePage && (
        <div className="header-title-area">
          {/* Not a heading element: the pages keep their own h1, and a second
              one in the header would double up the document outline.
              DJB-125: when the page has sub-pages, the title sits inside the
              toggle button so clicking it works exactly like the chevron. */}
          {subPages.length === 0 && (
            <span className="header-page-title">{activePage.label}</span>
          )}
          {subPages.length > 0 && (
            <>
              <button
                type="button"
                className="header-subpage-toggle"
                onClick={() => setAreSubPagesVisible((v) => !v)}
                aria-expanded={areSubPagesVisible}
                aria-label={`${areSubPagesVisible ? 'Hide' : 'Show'} ${activePage.label} sub-pages`}
              >
                {/* DJB-217: "Page – Subpage", e.g. "Library – Browse". One
                    inline run of text (not separate flex items) so it reads,
                    copies and truncates as a single label. */}
                <span className="header-title-text">
                  <span className="header-page-title">{activePage.label}</span>
                  <span className="header-title-separator" aria-hidden="true">
                    {' – '}
                  </span>
                  <span className="header-subpage-title">{activeSubPage.label}</span>
                </span>
                <HeaderChevron open={areSubPagesVisible} />
              </button>
              {areSubPagesVisible && (
                <div className="header-subpages-reveal">
                  <ul className="header-subpages">
                    {subPages.map((subPage) => {
                      // Same resolution as the title: ?tab= match, else the
                      // first sub-page the route opens by default
                      const subActive = subPage.key === activeSubPage.key;
                      return (
                        <li key={subPage.key}>
                          <button
                            type="button"
                            className={`header-subpage-link${subActive ? ' is-active' : ''}`}
                            aria-current={subActive ? 'true' : undefined}
                            onClick={() => openSubPage(subPage)}
                          >
                            {subPage.label}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {/* DJB-122: mobile hamburger on the right opens the slide-in overlay;
          hidden on desktop where the rail replaces it */}
      {authData?.accessToken && (
        <div className="header-right">
          <button
            type="button"
            className="menu-icon"
            aria-label="Open navigation menu"
            aria-haspopup="true"
            aria-expanded={isMobileNavOpen}
            onClick={() => setIsMobileNavOpen(true)}
          >
            <img src="/burger-menu.svg" alt="" className="menu-icon-img" />
          </button>
          <MobileNavOverlay
            open={isMobileNavOpen}
            onClose={() => setIsMobileNavOpen(false)}
          />
        </div>
      )}
    </header>
  );
};

export default Header;
